import React, { useState, useEffect, useCallback } from 'react';
import { FiChevronDown, FiChevronRight, FiSearch, FiX, FiMail, FiPhone, FiBriefcase, FiCalendar } from 'react-icons/fi';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/api';
import './HierarchyView.css';

const HierarchyView = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [highlightedPath, setHighlightedPath] = useState(new Set());

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAll({ limit: 1000 });
      setEmployees(response.data.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load hierarchy');
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build tree from manager relationships
  const buildHierarchy = useCallback(() => {
    const employeeMap = new Map(employees.map(e => [e.id, e]));
    const children = new Map();
    const roots = [];

    employees.forEach(emp => {
      if (emp.managerId) {
        if (!children.has(emp.managerId)) {
          children.set(emp.managerId, []);
        }
        children.get(emp.managerId).push(emp);
      } else {
        roots.push(emp);
      }
    });

    return { employeeMap, children, roots };
  }, [employees]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setHighlightedPath(new Set());
      return;
    }

    const { employeeMap, children, roots } = buildHierarchy();
    const lowerQuery = query.toLowerCase();
    const newExpanded = new Set();
    const newHighlighted = new Set();

    // Find matching employees
    const matches = employees.filter(emp => {
      const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
      return fullName.includes(lowerQuery);
    });

    // For each match, expand path to root
    matches.forEach(match => {
      newHighlighted.add(match.id);
      let current = match;

      while (current) {
        newExpanded.add(current.id);
        if (current.managerId) {
          current = employeeMap.get(current.managerId);
        } else {
          break;
        }
      }
    });

    setExpandedNodes(newExpanded);
    setHighlightedPath(newHighlighted);
  }, [employees, buildHierarchy]);

  const handleToggleExpand = (employeeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setHighlightedPath(new Set());
  };

  const { employeeMap, children, roots } = buildHierarchy();

  const renderNode = (employee) => {
    const subordinates = children.get(employee.id) || [];
    const isExpanded = expandedNodes.has(employee.id);
    const isHighlighted = highlightedPath.has(employee.id);
    const isCurrent = user && employee.id === user.id;

    return (
      <div
        key={employee.id}
        className={`hierarchy-node ${isHighlighted ? 'highlighted' : ''} ${isCurrent ? 'current-user' : ''}`}
      >
        <div
          className="node-content"
          onClick={() => handleSelectEmployee(employee)}
        >
          {subordinates.length > 0 && (
            <button
              className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand(employee.id);
              }}
            >
              {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            </button>
          )}

          {subordinates.length === 0 && <div className="expand-placeholder"></div>}

          <div className="node-card">
            <div className="node-avatar" style={{ backgroundColor: generateColor(employee.id) }}>
              {employee.firstName?.[0]?.toUpperCase()}
              {employee.lastName?.[0]?.toUpperCase()}
            </div>
            <div className="node-info">
              <h3 className="node-name">
                {employee.firstName} {employee.lastName}
              </h3>
              <p className="node-title">{employee.jobTitle || 'No title assigned'}</p>
              <p className="node-department">{employee.Department?.name || 'No department'}</p>
            </div>
          </div>
        </div>

        {isExpanded && subordinates.length > 0 && (
          <div className="node-children">
            {subordinates.map(child => renderNode(child))}
          </div>
        )}
      </div>
    );
  };

  const generateColor = (id) => {
    const colors = [
      '#3b82f6',
      '#ef4444',
      '#10b981',
      '#f59e0b',
      '#8b5cf6',
      '#ec4899',
      '#06b6d4',
      '#6366f1',
    ];
    const hash = String(id).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (loading) {
    return (
      <div className="hierarchy-view">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading organization hierarchy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hierarchy-view">
        <div className="error-banner">{error}</div>
      </div>
    );
  }

  return (
    <div className="hierarchy-view">
      <div className="hierarchy-header">
        <h1>Organization Hierarchy</h1>
        <p className="hierarchy-subtitle">Click on an employee to view details</p>
      </div>

      <div className="hierarchy-search">
        <FiSearch className="search-icon" />
        <input
          type="text"
          placeholder="Search by employee name..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
        {searchQuery && (
          <button className="clear-btn" onClick={handleClearSearch}>
            <FiX />
          </button>
        )}
      </div>

      {searchQuery && highlightedPath.size === 0 && (
        <div className="no-results">
          <p>No employees found matching "{searchQuery}"</p>
        </div>
      )}

      <div className="hierarchy-tree">
        {roots.length === 0 ? (
          <div className="no-data">
            <p>No employees found</p>
          </div>
        ) : (
          roots.map(root => renderNode(root))
        )}
      </div>

      {selectedEmployee && (
        <div className="employee-sidebar">
          <div className="sidebar-header">
            <h2>Employee Details</h2>
            <button
              className="close-btn"
              onClick={() => setSelectedEmployee(null)}
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="sidebar-content">
            <div className="employee-avatar" style={{ backgroundColor: generateColor(selectedEmployee.id) }}>
              {selectedEmployee.firstName?.[0]?.toUpperCase()}
              {selectedEmployee.lastName?.[0]?.toUpperCase()}
            </div>

            <h3 className="employee-name">
              {selectedEmployee.firstName} {selectedEmployee.lastName}
            </h3>

            <div className="detail-section">
              <h4>Job Information</h4>
              <div className="detail-item">
                <FiBriefcase className="detail-icon" />
                <div>
                  <span className="label">Job Title</span>
                  <span className="value">{selectedEmployee.jobTitle || 'Not assigned'}</span>
                </div>
              </div>
              <div className="detail-item">
                <FiBriefcase className="detail-icon" />
                <div>
                  <span className="label">Department</span>
                  <span className="value">{selectedEmployee.Department?.name || 'Not assigned'}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Contact Information</h4>
              <div className="detail-item">
                <FiMail className="detail-icon" />
                <div>
                  <span className="label">Email</span>
                  <span className="value">{selectedEmployee.email}</span>
                </div>
              </div>
              <div className="detail-item">
                <FiPhone className="detail-icon" />
                <div>
                  <span className="label">Phone</span>
                  <span className="value">{selectedEmployee.phone || 'Not provided'}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Employment Details</h4>
              <div className="detail-item">
                <FiCalendar className="detail-icon" />
                <div>
                  <span className="label">Join Date</span>
                  <span className="value">
                    {selectedEmployee.joinDate
                      ? format(new Date(selectedEmployee.joinDate), 'MMM d, yyyy')
                      : 'Not set'}
                  </span>
                </div>
              </div>
              {selectedEmployee.managerId && (
                <div className="detail-item">
                  <FiBriefcase className="detail-icon" />
                  <div>
                    <span className="label">Manager</span>
                    <span className="value">
                      {employeeMap.get(selectedEmployee.managerId)
                        ? `${employeeMap.get(selectedEmployee.managerId).firstName} ${
                            employeeMap.get(selectedEmployee.managerId).lastName
                          }`
                        : 'Unknown'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {selectedEmployee.address && (
              <div className="detail-section">
                <h4>Address</h4>
                <p className="value">{selectedEmployee.address}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchyView;
